<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NPopconfirm } from 'naive-ui'
import { useSourcesStore } from '../stores/sources'
import type { SourceRow } from '../stores/source_activity'
import { formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import ActivityRowDetail from './ActivityRowDetail.vue'
import {
  ingestIsInFlight,
  ingestKindLabel,
  ingestOutcome,
  ingestRowHasDetail,
  ingestRowTitle,
  ingestStatusPill,
} from './IngestActivityRow.helpers'

// One Source row in the Add-to-brain drawer's activity list. In-flight
// rows render as a recessed card with an Ingesting / Queued pill and a
// Cancel control; terminal rows render as a divider-separated line with
// a Done / Failed pill and the outcome (or the error) underneath. Either
// expands into ActivityRowDetail for the full prompt / answer / pages.
// The Source API carries no per-step progress, so the in-flight card
// shows a status pill rather than a step list.
const props = defineProps<{
  row: SourceRow
  // 1-indexed position inside the queued slice and the queued count —
  // the parent owns queue ordering so this row doesn't need the store.
  queuePosition?: number
  queueDepth?: number
}>()

const emit = defineEmits<{
  (e: 'open-path', path: string): void
}>()

const sources = useSourcesStore()
const now = useNow()
const expanded = ref(false)
const cancelling = ref(false)
const cancelError = ref<string | null>(null)

const inFlight = computed(() => ingestIsInFlight(props.row.status))
const pill = computed(() => ingestStatusPill(props.row.status))
const title = computed(() => ingestRowTitle(props.row))
const outcome = computed(() => ingestOutcome(props.row))
const hasDetail = computed(() => ingestRowHasDetail(props.row))
const failed = computed(() => props.row.status === 'failed')

// Meta line under the title: kind, then the queue position for a queued
// row or the outcome for a finished one, then when it last moved.
const queueNote = computed(() =>
  props.queuePosition && (props.queueDepth ?? 0) > 1 ? `Position ${props.queuePosition} of ${props.queueDepth}` : '',
)
const when = computed(() => formatDate(props.row.updated_at ?? props.row.created_at, now.value))

function toggleExpand(): void {
  if (hasDetail.value) expanded.value = !expanded.value
}

async function onCancelConfirm(): Promise<void> {
  cancelling.value = true
  cancelError.value = null
  try {
    // The row moves to terminal via the SourceChannel broadcast; the next
    // drawer-open load() reconciles if that broadcast is lost.
    await sources.cancel(props.row.id)
  } catch (e) {
    cancelError.value = e instanceof Error ? e.message : 'cancel failed'
  } finally {
    cancelling.value = false
  }
}
</script>

<template>
  <li
    class="flex flex-col gap-2"
    :class="
      inFlight ? 'rounded-xl border border-border-subtle bg-surface-bg p-3.5' : 'border-t border-border-divider py-1.5'
    "
    data-testid="ingest-activity-row"
  >
    <!-- The Cancel control is a sibling of the expand toggle, not nested
         in it: buttons inside buttons are invalid HTML. -->
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0 text-left text-ink disabled:cursor-default"
        :disabled="!hasDetail"
        :aria-expanded="hasDetail ? expanded : undefined"
        @click="toggleExpand"
      >
        <span
          aria-hidden="true"
          class="inline-block w-3 shrink-0 select-none text-ink-faint transition-transform"
          :class="{ 'rotate-90': expanded, invisible: !hasDetail }"
          >›</span
        >
        <span class="flex min-w-0 flex-1 flex-col gap-0.5">
          <span class="truncate font-medium" data-testid="ingest-activity-title">{{ title }}</span>
          <span class="text-xs leading-snug text-ink-faint [overflow-wrap:anywhere]">
            {{ ingestKindLabel(row.kind) }}
            <template v-if="queueNote"> · {{ queueNote }}</template>
            <template v-if="outcome">
              ·
              <span :class="failed ? 'text-status-alert' : ''" data-testid="ingest-activity-outcome">{{
                outcome
              }}</span>
            </template>
            <template v-if="when"> · {{ when }}</template>
          </span>
        </span>
        <span
          class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold"
          :class="pill.tone"
          data-testid="ingest-activity-pill"
        >
          <span v-if="inFlight" aria-hidden="true" class="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
          {{ pill.label }}
        </span>
      </button>

      <NPopconfirm v-if="inFlight" placement="left" @positive-click="onCancelConfirm">
        <template #trigger>
          <NButton
            size="small"
            quaternary
            type="error"
            class="min-h-11"
            :loading="cancelling"
            :data-testid="`cancel-${row.id}`"
            aria-label="Cancel this ingestion"
            >Cancel</NButton
          >
        </template>
        Cancel this ingestion? In-flight work will be discarded.
      </NPopconfirm>
    </div>

    <p v-if="cancelError" class="m-0 text-xs text-status-alert">{{ cancelError }}</p>

    <ActivityRowDetail
      v-if="expanded && hasDetail"
      :row="row"
      :queue-position="queuePosition"
      :queue-depth="queueDepth"
      @open-path="emit('open-path', $event)"
    />
  </li>
</template>
